import { CustomFieldProps, Identifiable, RecordModel, RecordsetFilters } from "../types/core";
import FormDrawer from "./FormDrawer";
import usePaginatedRecordList from "../hooks/usePaginatedRecordList";
import RecordFieldsMapper from "./RecordFieldsMapper";
import ChangeLogDrawer from "./ChangeLogDrawer";
import React, { ReactNode, useEffect, useRef, useState } from "react";
import { PlusOutlined, EditOutlined, ExclamationCircleOutlined, HistoryOutlined } from "@ant-design/icons";
import { Button, Modal, Popconfirm, Tooltip } from "antd";

interface RecordDrawerProps<RecordType>{
  recordModel: RecordModel<RecordType>;
  customCreateFields?: { name: string, component: ReactNode | ((props: CustomFieldProps<RecordType>) => ReactNode) }[];
  saveAction?: any;
  afterSave?: (res: any) => void;
  afterChange?: (res: any) => void;
  recordsetFilters?: RecordsetFilters;
  customRecordName?: string;
  recordInstance?: RecordType | null;
  onClose?: any;
  drawerWidth?: number;
  disabled?: boolean;
  customButton?: any;
  customAction?: any;
  btnSize?: 'small' | 'middle' | 'large';
  btnType?: 'primary' | 'link' | 'default' | 'text' | 'ghost' | 'dashed';
  btnGhost?: boolean;
  startChildren?: ReactNode;
  endChildren?: ReactNode;
  skipAutoOpen?: boolean;
  btnStyle?: any;
  centerFieldsStyle?: any;
  startFieldsStyle?: any;
  extraQueryAppend?: string;
  visibleFields?: string[];
  allowDelete?: boolean;
  forceOpen?: boolean;
}

const iso8601Regex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3,})?(Z)?$/;

export const nestedRecordToFormData = (record: any, schema?: any) => {
  let formData: any = {};
  let nestedData: any = {};
  Object.entries(record).forEach(([k, v]: [string, any]) => {
    let value = v;
    if (typeof v === 'object') {
      value = v?.id?.toString();
    }
    if (typeof v === 'number') {
      value = v?.toString();
    }
    if (typeof v === 'string' && iso8601Regex.test(v)) {
      value = v.replace(/\.\d{3,}\d*Z$/, 'Z').replace("Z", "");
      console.log(v, value);
    }
    // related ids
    if (Array.isArray(v)) {
      value = v.map(i => {
        if (!i) return undefined;
        if (typeof i === 'object') {
          return i.id?.toString();
        }
        return i.toString();
      });
    }
    formData[k] = value;
  });
  if (!!schema) {
    Object.entries(schema).forEach(([k, v]: [string, any]) => {
      // Nested record
      if (v.type === 'nested object') {
        nestedData[k] = nestedRecordToFormData(record);
      }
      // Nested record array
      if (v.type === 'field' && v.child?.type === 'nested object') {
        nestedData[k] = record[k].map((obj: any) => nestedRecordToFormData(obj));
      }
    });
  }
  return { ...formData, ...nestedData };
}

function RecordDrawer<RecordType extends Identifiable>(props: RecordDrawerProps<RecordType>) {
  const {
    patchRecord,
    putRecord,
    createRecord,
    recordFields,
    fetchRecordLocalizedTexts,
    doRecordAction,
    contentType,
    deleteRecord
  } = usePaginatedRecordList<any>({
    recordPath: props.recordModel.path,
    apiService: 'admin',
    extraQueryAppend: props.extraQueryAppend,
    initialOptionsFetch: true,
    optionsRecordId: !!props.customAction?.path ? props.recordInstance?.id : undefined,
    optionsPath: props.customAction?.path,
  });
  const {
    recordModel,
    recordInstance,
    customCreateFields = [],
    skipAutoOpen = false,
    allowDelete = false,
    forceOpen = false,
  } = props;
  const formDrawerRef = useRef<any>();
  const [changeLogOpen, setChangeLogOpen] = useState(false);
  const isEdit = !!recordInstance && !props.customAction;
  const isAction = !!props.customAction;
  const saveAction = props.saveAction || (
    !!props.customAction ? (
      (id: any, data: any) => doRecordAction(props.customAction.path, props.customAction.method, data, props.recordInstance?.id)
    ) : (
      recordInstance ? (recordModel.useMultipartFormData ? putRecord : patchRecord) : createRecord
    )
  );
  const startChildren = props.startChildren || recordModel.createStartChildren;
  const modelEndChildren = recordModel.createEndChildren;
  const endChildren = props.endChildren || (
    typeof modelEndChildren === 'function' ? modelEndChildren(recordInstance ?? undefined) : modelEndChildren
  );
  const startFieldsStyle = props.startFieldsStyle || recordModel.createStartFieldsStyle;
  const centerFieldsStyle = props.centerFieldsStyle || recordModel.createCenterFieldsStyle;
  const splitFieldsAtIndex = props.recordModel.splitFieldsAtIndex;

  const handleDelete = () => {
    if (!!recordInstance) {
      console.log('>>>>> deleteRecord', recordInstance, !!props.afterChange);
      deleteRecord(recordInstance.id);
      formDrawerRef.current.close();
      if (!!props.afterChange) {
        console.log('>>>>> afterChange', recordInstance);
        props.afterChange(recordInstance);
      }
    }
  }

  useEffect(() => {
    if (!!props.recordsetFilters) {
      if (!recordInstance) {
        formDrawerRef.current.resetFields();
      }
      formDrawerRef.current.setFieldsValue(props.recordsetFilters);
    }
  }, [props.recordsetFilters]);

  useEffect(() => {
    formDrawerRef.current.setFieldsValue(props.recordModel.defaultValues);
  }, [props.recordModel]);

  useEffect(() => {
    if (isEdit) {
      if(!skipAutoOpen) {
        formDrawerRef.current.open();
      }
      formDrawerRef.current.setFieldsValue(nestedRecordToFormData(recordInstance, recordFields));

      // Get all language texts for edit
      if (Object.keys(recordFields || {}).some(recordKey => recordKey.includes('__'))) {
        const localizedTexts: any = {};
        Object.keys(recordFields || {}).filter(recordKey => recordKey.includes('__')).forEach(locKey => {
          localizedTexts[locKey] = null;
        });
        fetchRecordLocalizedTexts(Number(recordInstance.id)).then(texts => {
          texts.forEach(lt => localizedTexts[`${lt.field_name}__${lt.locale}`] = lt.text);
          formDrawerRef.current.setFieldsValue(localizedTexts);
        });
      }
    }
    if (isAction) {
      formDrawerRef.current.setFieldsValue(nestedRecordToFormData(recordInstance));
    }
  }, [recordInstance]);

  useEffect(() => {
    if (forceOpen) {
      formDrawerRef.current.open();
    }
  }, [forceOpen]);

  return (
    <div>
      <FormDrawer
        ref={formDrawerRef}
        customButton={!!props.customAction ? props.customButton : (!!props.customButton ? props.customButton : (recordInstance === undefined ? undefined : <></>))}
        drawerExtra={isEdit && recordModel.showsChangeLog ? (
          <Tooltip title="View change log">
            <Button
              type="text"
              icon={<HistoryOutlined />}
              onClick={() => setChangeLogOpen(true)}
            />
          </Tooltip>
        ) : undefined}
        recordId={recordInstance ? (recordInstance as any)[props.recordModel.primaryKeyName || 'id'] : undefined}
        onClose={props.onClose}
        actionName={isAction ? (props.customAction.name || 'Perform') : isEdit ? 'Edit' : 'Create'}
        recordName={props.customRecordName || recordModel.name}
        btnIcon={recordInstance ? <EditOutlined /> : <PlusOutlined />}
        saveButtonText={recordInstance ? 'Save' : undefined}
        handleRecordAction={saveAction}
        afterSave={props.afterSave || props.afterChange}
        primaryKeyName={recordModel.primaryKeyName}
        drawerWidth={props.drawerWidth || recordModel.drawerWidth}
        afterFormReset={() => {
          if (!!props.recordsetFilters) {
            formDrawerRef.current.setFieldsValue(props.recordsetFilters);
          }
        }}
        disabled={props.disabled}
        useMultipartFormData={recordModel.useMultipartFormData}
        btnType={props.btnType}
        btnSize={props.btnSize}
        btnGhost={props.btnGhost}
        btnStyle={props.btnStyle}
        extraActions={(isEdit && allowDelete) ? (
          <Popconfirm
            title="Are you sure?"
            onConfirm={handleDelete}
            okText="Yes"
            cancelText="No"
            okType="danger"
          >
            <Button danger>
              Delete
            </Button>
        </Popconfirm>
        ) : undefined}
      >
        <div style={recordModel.formWrapperStyle}>
        {startChildren}

        {splitFieldsAtIndex && (
          <div style={startFieldsStyle}>
            <RecordFieldsMapper<RecordType>
              fieldDefinitions={!!recordFields ? Object.fromEntries(Object.entries(recordFields).slice(0, splitFieldsAtIndex + 1)) : undefined}
              customCreateFields={[...(recordModel.customCreateFields ? recordModel.customCreateFields : []), ...customCreateFields]}
              recordModel={recordModel}
              setFieldsValue={formDrawerRef.current?.setFieldsValue}
              recordsetFilters={props.recordsetFilters}
              formRef={formDrawerRef}
              recordInstance={!!recordInstance ? recordInstance : undefined}
              contentType={contentType}
              arrayNestedObjectsCount={2}
              visibleFields={props.visibleFields}
            />
          </div>
        )}

        <div style={centerFieldsStyle}>
          <RecordFieldsMapper<RecordType>
            fieldDefinitions={splitFieldsAtIndex ? (!!recordFields ? Object.fromEntries(Object.entries(recordFields).slice(splitFieldsAtIndex+1)) : undefined) : recordFields}
            customCreateFields={[...(recordModel.customCreateFields ? recordModel.customCreateFields : []) , ...customCreateFields]}
            recordModel={recordModel}
            setFieldsValue={formDrawerRef.current?.setFieldsValue}
            recordsetFilters={props.recordsetFilters}
            formRef={formDrawerRef}
            recordInstance={!!recordInstance ? recordInstance : undefined}
            contentType={contentType}
            arrayNestedObjectsCount={2}
            visibleFields={props.visibleFields}
          />
        </div>
        {endChildren}
        </div>
      </FormDrawer>
      {isEdit && recordModel.showsChangeLog && recordInstance && (
        <ChangeLogDrawer
          recordPath={recordModel.path}
          recordId={(recordInstance as any)[recordModel.primaryKeyName || 'id']}
          recordName={recordModel.toString(recordInstance)}
          fieldLabels={Object.fromEntries(
            Object.entries(recordFields || {}).map(([k, v]: any) => [k, v?.label || k])
          )}
          open={changeLogOpen}
          onClose={() => setChangeLogOpen(false)}
        />
      )}
    </div>
  )
}

export default RecordDrawer;
